import { computed, isRef } from 'vue';
import { useFieldLimits } from '../../Registration/composables/useFieldLimits';
import {useVisibilityRules} from "../../Registration/composables/useVisibilityRules";
import {useBookingStore} from "../../Registration/stores/bookingStore";
import {useCheckoutFieldLimitsStore} from "../../Registration/stores/checkoutFieldLimitsStore";
import { useI18n } from 'vue-i18n';

export function useProcessedOptions(item, options, getPrefix, isReadOnly, fieldCurrentValue = null, group, groupId, t) {

    const {evaluateVisibilityRules} = useVisibilityRules();
    const { shouldDisableFieldOption, getFieldOptionAvailabilityText, getFieldCurrentValue } = useFieldLimits(t);
    const { t: translate } = useI18n();

    const checkoutFieldLimitsStore = useCheckoutFieldLimitsStore();
    const store = useBookingStore();

    const processedOptions = computed(() => {
        // Explicitly access store.data to ensure reactivity to booking changes
        const bookingData = store.data;

        const prefix = getPrefix();

        // Resolve fieldCurrentValue: supports refs (from useFormField's model) and plain values
        const resolvedFieldValue = isRef(fieldCurrentValue) ? fieldCurrentValue.value : fieldCurrentValue;
        const currentValue = resolvedFieldValue ?? getFieldCurrentValue(item.form_mapping, prefix);

        // Always filter by visibility rules first, regardless of limits
        const visibleOptions = options.value?.filter((option) => {
            let companionIndex = null;
            if (group === 'companions' && groupId && bookingData.companions) {
                companionIndex = bookingData.companions.findIndex(c => c.uniqueId === groupId);
                companionIndex = companionIndex >= 0 ? companionIndex : null;
            }
            return evaluateVisibilityRules(option.visibility_rules, companionIndex);
        }) || [];

        // Determine if limits are enabled for this field
        // Checkout context: store has data from the field-availabilities API
        // Registration context: fall back to item.limits?.enabled
        const isCheckoutContext = checkoutFieldLimitsStore.isFieldLimitsEnabled(item.core_id);
        const limitsEnabled = isCheckoutContext || item.limits?.enabled;

        // If limits are not enabled, return simplified options
        if (!limitsEnabled) {
            return visibleOptions.map(option => ({
                ...option,
                disabled: isReadOnly.value,
                displayLabel: option.label
            }));
        }

        // Process options with limits enabled — checkout context
        if (isCheckoutContext) {
            return visibleOptions.map(option => {
                const isCurrentlySelected = Array.isArray(currentValue)
                    ? currentValue.includes(option.key)
                    : currentValue === option.key;

                // Don't disable if option is currently selected by this guest
                const shouldDisable = isCurrentlySelected
                    ? false
                    : checkoutFieldLimitsStore.isOptionDisabled(item.core_id, option.key);

                let availabilityText = '';
                if (item.display_availability) {
                    availabilityText = checkoutFieldLimitsStore.getAvailabilityText(
                        item.core_id,
                        option.key,
                        translate,
                        isCurrentlySelected
                    );
                }

                return {
                    ...option,
                    disabled: shouldDisable || isReadOnly.value,
                    availabilityText,
                    displayLabel: option.label + (availabilityText ? ` ${availabilityText}` : '')
                };
            });
        }

        // Registration context: use the existing useFieldLimits composable
        return visibleOptions.map(option => {
            const shouldDisable = shouldDisableFieldOption(
                item.form_mapping,
                option.key,
                option.core_id,
                prefix,
                item.core_id
            );

            let availabilityText = '';
            if (item.display_availability) {
                availabilityText = getFieldOptionAvailabilityText(
                    item.limits?.enabled || false,
                    item.core_id,
                    item.form_mapping,
                    option.key,
                    option.core_id,
                    prefix
                );
            }

            return {
                ...option,
                disabled: shouldDisable || isReadOnly.value,
                availabilityText,
                displayLabel: option.label + (availabilityText ? ` ${availabilityText}` : '')
            };
        });
    });

    return { processedOptions };
}
